import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Star, Camera, X, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api, resolveImg, uploadReviewImage, formatApiError } from "@/lib/api";

const inputCls = "w-full bg-transparent border-b border-ptf-primary/25 py-2.5 text-sm outline-none transition-colors duration-300 focus:border-ptf-secondary placeholder:text-ptf-text/40 text-ptf-text";

function Stars({ value, onChange, size = "h-5 w-5" }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        onChange ? (
          <button key={n} type="button" data-testid={`star-${n}`} onClick={() => onChange(n)} aria-label={`${n} star${n > 1 ? "s" : ""}`} className="text-ptf-primary/30 hover:scale-110 transition-transform">
            <Star className={`${size} ${n <= value ? "fill-ptf-accent text-ptf-accent" : ""}`} />
          </button>
        ) : (
          <Star key={n} className={`${size} ${n <= value ? "fill-ptf-accent text-ptf-accent" : "text-ptf-primary/20"}`} />
        )
      ))}
    </div>
  );
}

function ReviewForm({ journeyId, onDone }) {
  const [form, setForm] = useState({ name: "", text: "" });
  const [rating, setRating] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = async (files) => {
    const room = 3 - photos.length;
    const list = Array.from(files).slice(0, room);
    if (list.length === 0) return;
    setBusy(true);
    try {
      const up = [];
      for (const f of list) up.push(await uploadReviewImage(f));
      setPhotos((p) => [...p, ...up]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Photo upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) { toast.error("Please pick a star rating."); return; }
    setLoading(true);
    try {
      const { data } = await api.post(`/journeys/${journeyId}/reviews`, { ...form, rating, photos });
      toast.success(data.message);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit the review.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="review-form" className="border border-ptf-primary/12 bg-white p-6 md:p-8 space-y-5">
      <p className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-primary/50">Travelled with us? Share your experience</p>
      <div className="flex items-center gap-4">
        <Stars value={rating} onChange={setRating} size="h-6 w-6" />
        {rating > 0 && <span className="text-xs text-ptf-text/55">{rating}/5</span>}
      </div>
      <input required data-testid="rv-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} placeholder="Your name *" />
      <textarea required rows={3} data-testid="rv-text" value={form.text} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} className={`${inputCls} resize-none`} placeholder="What stayed with you from the journey? *" />
      <div className="flex flex-wrap items-center gap-3">
        {photos.map((p, i) => (
          <div key={p} className="relative h-16 w-20 overflow-hidden border border-ptf-primary/15 group">
            <img src={resolveImg(p)} alt="" className="h-full w-full object-cover" />
            <button type="button" data-testid="rv-remove-photo" onClick={() => setPhotos((prev) => prev.filter((_, idx) => idx !== i))} className="absolute top-0.5 right-0.5 grid place-items-center h-5 w-5 bg-ptf-primary/80 text-white opacity-0 group-hover:opacity-100 transition-opacity">
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        {photos.length < 3 && (
          <button type="button" data-testid="rv-add-photo" onClick={() => inputRef.current?.click()} disabled={busy} className="inline-flex items-center gap-2 border border-dashed border-ptf-primary/30 px-4 py-2.5 text-xs uppercase tracking-[0.12em] text-ptf-secondary hover:bg-ptf-primary/5 transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            Add photos ({photos.length}/3)
          </button>
        )}
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
      </div>
      <button type="submit" disabled={loading} data-testid="rv-submit" className="inline-flex items-center gap-2 bg-ptf-primary text-ptf-bg px-7 py-3 text-xs font-semibold uppercase tracking-[0.18em] hover:bg-ptf-secondary transition-colors disabled:opacity-60">
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        Submit Review
      </button>
      <p className="text-[0.65rem] text-ptf-text/50">Reviews appear after a quick check by our team.</p>
    </form>
  );
}

export default function JourneyReviews({ journeyId }) {
  const qc = useQueryClient();
  const [submitted, setSubmitted] = useState(false);
  const { data: reviews = [] } = useQuery({
    queryKey: ["reviews", journeyId],
    queryFn: async () => (await api.get(`/journeys/${journeyId}/reviews`)).data,
  });

  return (
    <div data-testid="journey-reviews" className="pt-10">
      <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-4">
        Traveller reviews{reviews.length > 0 ? ` (${reviews.length})` : ""}
      </p>
      {reviews.length > 0 && (
        <div className="space-y-4 mb-8">
          {reviews.map((r) => (
            <div key={r.id} data-testid="review-item" className="border border-ptf-primary/12 bg-white p-6">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-medium text-ptf-primary">{r.name}</p>
                <Stars value={r.rating} size="h-4 w-4" />
              </div>
              <p className="mt-3 text-sm text-ptf-text/75 leading-relaxed">{r.text}</p>
              {r.photos?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-3">
                  {r.photos.map((p) => (
                    <div key={p} className="h-24 w-32 overflow-hidden">
                      <img src={resolveImg(p)} alt={`Photo by ${r.name}`} className="h-full w-full object-cover" loading="lazy" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {submitted ? (
        <div data-testid="review-submitted" className="border border-ptf-primary/12 bg-white p-6 flex items-start gap-4">
          <span className="grid place-items-center h-10 w-10 rounded-full bg-ptf-accent text-ptf-primary shrink-0"><Check className="h-5 w-5" /></span>
          <div>
            <p className="font-medium text-ptf-primary">Thank you for your review.</p>
            <p className="text-sm text-ptf-text/70 mt-1">It will appear here once our team approves it.</p>
          </div>
        </div>
      ) : (
        <ReviewForm journeyId={journeyId} onDone={() => { setSubmitted(true); qc.invalidateQueries({ queryKey: ["reviews", journeyId] }); }} />
      )}
    </div>
  );
}
